import { Area } from './Area';
import * as d3 from 'd3';
import React from 'react';

export interface SparklineProps<D extends {}> extends React.SVGProps<SVGSVGElement> {
  data: D[];
  xAccessor?: (d: D) => Date;
  yAccessor?: (d: D) => number;
  width: number;
  height: number;
  color: React.ComponentPropsWithoutRef<typeof Area>['color'];
  margin?: { top: number; right: number; bottom: number; left: number };
}

export const Sparkline = <D extends {}>({
  data,
  xAccessor = () => new Date(),
  yAccessor = d => Number(d),
  width,
  height,
  color,
  margin = { top: 4, right: 4, bottom: 4, left: 4 },
  ...props
}: SparklineProps<D>) => {
  if (!data || data.length === 0) {
    return null;
  }

  const xDomain = [xAccessor(data[0]!), xAccessor(data[data.length - 1]!)];
  const yDomain = [0, d3.max(data, yAccessor) || 100];

  const x = d3
    .scaleTime()
    .domain(xDomain)
    .range([margin.left, width - margin.right]);
  const y = d3
    .scaleLinear()
    .domain(yDomain)
    .range([height - margin.bottom, margin.top]);

  const line = d3
    .line<D>()
    .x(d => x(xAccessor(d)))
    .y(d => y(yAccessor(d)));

  const area = d3
    .area<D>()
    .x(d => x(xAccessor(d)))
    .y0(height)
    .y1(d => y(yAccessor(d)));

  return (
    <svg data-component="Sparkline" width={width} height={height} {...props}>
      <Area line={line} area={area} data={data} color={color} />
    </svg>
  );
};
